/**
 * The overflow menu's by-role table, verbatim from `26-pool-members.md`'s
 * "The overflow menu, by role", including the `Revive` row on an eliminated
 * member that R3 and D3 exist for. This is a client-side permission table,
 * so it is decided here and never inside a component. ISSUE-817 wired five of
 * these ids to real endpoints (`makeAdmin`, `makeMember`, `suspend`,
 * `unsuspend`, `removeFromPool`). Every other id is wired in `PoolMembersPane`
 * too (ISSUE-1045 finished `message` and `report`), so every row this table
 * returns is real.
 */
import type { typeMemberContestStatus } from "./memberRowFacts";

export type typeMemberMenuActionId =
  | "viewProfile"
  | "message"
  | "block"
  | "report"
  | "giveAward"
  | "revive"
  | "makeAdmin"
  | "makeMember"
  | "suspend"
  | "unsuspend"
  | "removeFromPool"
  | "leavePool";

export type typeMemberMenuItem = {
  id: typeMemberMenuActionId;
  label: string;
  destructive: boolean;
};

const MENU_LABELS: Record<typeMemberMenuActionId, string> = {
  viewProfile: "View profile",
  message: "Message",
  block: "Block",
  report: "Report",
  giveAward: "Give an award",
  revive: "Revive",
  makeAdmin: "Make Admin",
  makeMember: "Make Member",
  suspend: "Suspend",
  unsuspend: "Unsuspend",
  removeFromPool: "Remove from pool",
  leavePool: "Leave pool",
};

const DESTRUCTIVE_IDS: readonly typeMemberMenuActionId[] = [
  "block",
  "report",
  "suspend",
  "removeFromPool",
  "leavePool",
];

function toMenuItems(
  ids: readonly typeMemberMenuActionId[],
): typeMemberMenuItem[] {
  return ids.map((id) => ({
    id,
    label: MENU_LABELS[id],
    destructive: DESTRUCTIVE_IDS.includes(id),
  }));
}

export function buildMemberOverflowMenu(input: {
  viewerUserId: string;
  viewerPoolRole: "owner" | "admin" | "member";
  targetUserId: string;
  targetPoolRole: "owner" | "admin" | "member";
  targetStatus:
    "pending" | "active" | "suspended" | "left" | "removed" | "rejected";
  targetContestStatus?: typeMemberContestStatus | null;
}): typeMemberMenuItem[] {
  const viewerIsOfficer =
    input.viewerPoolRole === "owner" || input.viewerPoolRole === "admin";

  // ISSUE-817. The client never offers an action pool-api will refuse, so both
  // server gates are mirrored here rather than left for a VALIDATION_ERROR to
  // catch after the fan has already pressed:
  //
  //   - `/member/set-role` is owner-only
  //     (`postMemberSetRoleHandler` -> `requirePoolRole({ required: "owner" })`),
  //   - `/member/suspend`, `/member/unsuspend` and `/member/remove` go through
  //     `canModerate`, a STRICT rank comparison, so an Admin may not act on
  //     another Admin (`FORBIDDEN_ROLE`, reason `targetOutranksActor`).
  //
  // `canModerateTarget` is that second rule, and it is applied on every branch
  // that offers one of those three, not only on the plain-member branch.
  const isOwnerViewer = input.viewerPoolRole === "owner";
  const canModerateTarget = isOwnerViewer || input.targetPoolRole === "member";

  if (input.targetUserId === input.viewerUserId) {
    return toMenuItems(["leavePool"]);
  }

  if (!viewerIsOfficer) {
    return toMenuItems(["viewProfile", "message", "block", "report"]);
  }

  if (input.targetPoolRole === "owner") {
    return toMenuItems(["viewProfile", "giveAward"]);
  }

  if (input.targetStatus === "suspended") {
    if (!canModerateTarget) {
      return toMenuItems(["viewProfile", "giveAward"]);
    }
    return toMenuItems(["viewProfile", "unsuspend", "removeFromPool"]);
  }

  // unavailable deliberately does not offer Revive, because POST
  // /entry/revive lives in contest-api and would fail while contest-api is
  // down, and because we do not know the member is eliminated. The pane's
  // banner says why instead. ISSUE-706.
  if (input.targetContestStatus === "eliminated") {
    if (!canModerateTarget) {
      return toMenuItems(["viewProfile", "revive", "giveAward"]);
    }
    return toMenuItems([
      "viewProfile",
      "revive",
      "giveAward",
      "removeFromPool",
    ]);
  }

  if (input.targetPoolRole === "admin") {
    // Only the Commissioner may demote an Admin, and only the Commissioner
    // outranks one, so an Admin viewer is left with the two actions that need
    // no rank.
    if (!isOwnerViewer) {
      return toMenuItems(["viewProfile", "giveAward"]);
    }
    return toMenuItems([
      "viewProfile",
      "giveAward",
      "makeMember",
      "suspend",
      "removeFromPool",
    ]);
  }

  // A plain member target. An Admin outranks them, so suspend and remove both
  // succeed; only the promotion is owner-only.
  if (!isOwnerViewer) {
    return toMenuItems([
      "viewProfile",
      "giveAward",
      "suspend",
      "removeFromPool",
    ]);
  }
  return toMenuItems([
    "viewProfile",
    "giveAward",
    "makeAdmin",
    "suspend",
    "removeFromPool",
  ]);
}
